/**
=========================================================
* Soft UI Dashboard PRO React - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { Fragment, useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SuiBox from "components/sui/SuiBox";
import SuiTypography from "components/sui/SuiTypography";

// Soft UI Dashboard PRO React example components
import SidenavCollapse from "components/cdx/CdxSidenav/SidenavCollapse";
import SidenavList from "components/cdx/CdxSidenav/SidenavList";
import SidenavItem from "components/cdx/CdxSidenav/SidenavItem";

// Custom styles for the CdxSidenav
import SidenavRoot from "components/cdx/CdxSidenav/SidenavRoot";
import sidenavLogoLabel from "components/cdx/CdxSidenav/styles/sidenav";

// Soft UI Dashboard PRO React context
import { useSoftUIController, setMiniSidenav } from "context";

import { useTranslation } from "react-i18next";

function CdxSidenav({ color, brand, brandName, routes, ...rest }) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;

  const { t } = useTranslation();

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200 || miniSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse) => {
    const template = collapse.map(
      ({ name, restrictTranslation, path, key, href }) =>
        href ? (
          <Link
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem name={restrictTranslation ? name : t(name)} nested />
          </Link>
        ) : (
          <NavLink to={path} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem
              name={restrictTranslation ? name : t(name)}
              active={path === pathname}
              nested
            />
          </NavLink>
        )
    );

    return template;
  };

  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses) =>
    collapses.map(
      ({ name, restrictTranslation, collapse, path, href, key }) => {
        let returnValue;

        if (collapse) {
          returnValue = (
            <SidenavItem
              key={key}
              name={restrictTranslation ? name : t(name)}
              active={pathname.startsWith(path)}
              open={openNestedCollapse === name}
              onClick={() =>
                openNestedCollapse === name
                  ? setOpenNestedCollapse(false)
                  : setOpenNestedCollapse(name)
              }
            >
              {renderNestedCollapse(collapse)}
            </SidenavItem>
          );
        } else {
          returnValue = href ? (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavItem
                name={restrictTranslation ? name : t(name)}
                active={pathname.startsWith(path)}
              />
            </Link>
          ) : (
            <NavLink to={path} key={key} sx={{ textDecoration: "none" }}>
              <SidenavItem
                name={restrictTranslation ? name : t(name)}
                active={pathname.startsWith(path)}
              />
            </NavLink>
          );
        }
        return <SidenavList key={key}>{returnValue}</SidenavList>;
      }
    );

  // Render all the routes from the routes.js (All the visible items on the CdxSidenav)
  const renderRoutes = routes.map(
    ({
      type,
      name,
      restrictTranslation,
      icon,
      title,
      collapse,
      noCollapse,
      key,
      href,
      path,
    }) => {
      let returnValue;
      if (type === "collapse") {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              color={color}
              name={restrictTranslation ? name : t(name)}
              icon={icon}
              active={pathname.startsWith(path)}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <SidenavCollapse
            color={color}
            key={key}
            name={restrictTranslation ? name : t(name)}
            icon={icon}
            active={pathname.startsWith(path)}
            open={openCollapse === name}
            onClick={() =>
              openCollapse === name
                ? setOpenCollapse(false)
                : setOpenCollapse(name)
            }
          >
            {collapse ? renderCollapse(collapse) : null}
          </SidenavCollapse>
        );
      } else if (type === "nocollapse") {
        returnValue = (
          <NavLink to={path} key={key}>
            <SidenavCollapse
              color={color}
              name={restrictTranslation ? name : t(name)}
              icon={icon}
              active={pathname.startsWith(path)}
              noCollapse={true}
            />
          </NavLink>
        );
      } else if (type === "title") {
        returnValue = (
          <SuiBox key={key} textAlign={miniSidenav ? "center" : "left"}>
            <SuiTypography
              display="block"
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
              opacity={0.6}
              pl={miniSidenav ? 0 : 3}
              mt={2}
              mb={1}
              ml={miniSidenav ? 0 : 1}
            >
              {t(title)}
            </SuiTypography>
          </SuiBox>
        );
      } else if (type === "divider") {
        returnValue = <Divider key={key} />;
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, miniSidenav }}
    >
      <SuiBox pt={3} pb={1} px={4} textAlign="center">
        <SuiBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SuiTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SuiTypography>
        </SuiBox>
        <SuiBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && (
            <SuiBox
              component="img"
              src={brand}
              alt="Soft UI Logo"
              width="2rem"
            />
          )}
          <SuiBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <SuiTypography component="h6" variant="button" fontWeight="medium">
              {brandName}
            </SuiTypography>
          </SuiBox>
        </SuiBox>
      </SuiBox>
      <Divider />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

// Setting default values for the props of CdxSidenav
CdxSidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the CdxSidenav
CdxSidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default CdxSidenav;
